import { unionSchema, yup } from '@framework/validation';
import { ExpressionType } from '../../IExpression';
import { createExpressionType } from '../../IExpressionType';
import { expressionLogger } from '../../utils/expressionLogger';
import { ReferenceSchema } from '../../validation/ReferenceSchema';
import { AsyncExpressionType } from '../async/AsyncExpression.type';
import { FunctionExpressionType } from '../function/FunctionExpression.type';
import { OperatorsRepository } from '../operators/OperatorsRepository';
import { ReferenceExpressionType } from '../reference/ReferenceExpression.type';
import { SubconditionGroupExpressionSchema } from '../subconditions-group/SubconditionsGroupExpression.type';
import { ValueExpressionType } from '../value/ValueExpression.type';
import { IConditionArgument } from './IConditionArgument';
import { IConditionExpression } from './IConditionExpression';

export const ConditionExpressionType =
  createExpressionType<IConditionExpression>({
    type: ExpressionType.Condition,
    schema: yup.object({
      type: yup.string().exact(ExpressionType.Condition).required(),
      reference: ReferenceSchema.label('Riferimento').required(),
      operator: yup.string().label('Operatore').required(),
      arguments: yup
        .array<any, IConditionArgument>(
          unionSchema([
            ValueExpressionType.schema,
            ReferenceExpressionType.schema,
            FunctionExpressionType.schema,
            AsyncExpressionType.schema,
            SubconditionGroupExpressionSchema,
          ]) as yup.Lazy<IConditionArgument>,
        )
        .required(),
    }),
    /**
     * Valuta la condizione, restituendo true o false a seconda del risultato
     * dell'operatore.
     */
    evaluate(expression, scope) {
      // Il campo reference che dobbiamo confrontare
      const referenceValue = scope.getReference(expression.reference);

      // Creiamo uno scope figlio perché dobbiamo valutare le condizioni
      // che potrebbero essere delle SubconditionsGroupExpression
      const argsScope = scope.createChild(expression.reference);

      // I valori con cui confrontare il reference
      const argsValues = expression.arguments.map(argument =>
        argsScope.evaluate(argument),
      );

      const operator = OperatorsRepository.get(expression.operator)!;
      try {
        return operator.compare(referenceValue, argsValues, argsScope);
      } catch (error: unknown) {
        expressionLogger.error(`[ConditionExpression.evaluate] Si è verificato un errore nella valutazione dell'operatore ${expression.operator}`, { scope, error, argsValues, expression }); // prettier-ignore
        throw error;
      }
    },
    getReferences(expression, collect) {
      return [expression.reference, ...expression.arguments.flatMap(collect)];
    },
    toString(expression, toString) {
      return `@${expression.reference.code} ${
        expression.operator
      } ${expression.arguments.map(toString).join(', ')}`;
    },
  });
